import { Footer } from '@latitude/footer';
import { Header } from '@latitude/header';
import { Text } from '@latitude/text';
import { cn } from '@latitude/token-utils';
import type { Ref } from 'react';
import type { CardProps } from 'types';

import { cardVariants } from './variants/card-variants';

/**
 * A customizable card component supporting filled, outlined, and outlineFilled variants.
 *
 * Example:
 * <Card title="Title">Content</Card>
 * <Card title="Outlined" variant="outlined">Content</Card>
 * <Card title="Outline Filled" variant="outlineFilled">Content</Card>
 */
const Card = ({
  className,
  title,
  headerProps,
  footerProps,
  bodyClassName,
  children,
  rawProps = {},
  'data-testid': testID,
  onClick = undefined,
  variant = 'filled',
  noBorder,
  ref,
}: CardProps & {
  ref?: Ref<HTMLDivElement>;
}) => {
  const isClickable = onClick !== undefined;
  // A header or footer has to reach the card's edges, so the root drops its own
  // padding and the body carries it instead.
  const hasChrome = Boolean(headerProps || footerProps);

  // Built on demand: the `headerProps` path never renders it, and that is the
  // path this component is moving towards.
  const renderLegacyContent = () => (
    <>
      {title && (
        <div className="pb-sizing-sys-size-24">
          {typeof title === 'string' ? (
            <Text variant="headingSmall" color="strong">
              {title}
            </Text>
          ) : (
            title
          )}
        </div>
      )}
      {children}
    </>
  );

  return (
    <div
      onClick={onClick}
      data-testid={testID}
      ref={ref}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={(event) => {
        // Only the card's own key presses activate it. Enter/Space on a button
        // in the header or footer bubbles up here, and preventDefault would
        // cancel that button's activation before running the card's onClick in
        // its place.
        if (event.target !== event.currentTarget) {
          return;
        }
        if ((event.key === 'Enter' || event.key === ' ') && isClickable) {
          event.preventDefault();
          onClick(event);
        }
      }}
      className={cn(
        cardVariants({ variant, clickable: isClickable, noBorder }),
        hasChrome ? 'overflow-clip' : 'p-spacing-ref-default',
        className,
      )}
      {...rawProps}
    >
      {headerProps && <Header {...headerProps} />}

      {/* Chrome moves the padding off the root and onto the body, so the header
          and footer can reach the card's edges. */}
      {hasChrome ? (
        <div className={cn('p-spacing-ref-default', bodyClassName)}>
          {headerProps ? children : renderLegacyContent()}
        </div>
      ) : (
        renderLegacyContent()
      )}

      {footerProps && <Footer {...footerProps} />}
    </div>
  );
};

Card.displayName = 'Card';

export { Card, type CardProps };
